/**
 * Centralized OAuth error message definitions.
 *
 * These error codes come from different backend authentication flows:
 *
 * ## ProviderCallback errors (adapter.on_authentication_error):
 * - `cancelled`: User cancelled the OAuth flow
 * - `denied`: Provider denied access
 * - `unknown`: Generic/unhandled errors
 * - `consumer_account_not_supported`: Microsoft personal account rejected (M365 only)
 *
 * ## OrgOnboarding errors (various OAuth flows):
 * - `connected_other`: Account already connected to another user
 * - `access_denied`: User denied OAuth permissions
 * - `invalid_request`: Malformed OAuth request
 */

/**
 * Error message definitions for OAuth authentication flows.
 *
 * ProviderCallback uses title/message format for full error pages.
 * OrgOnboarding uses message field for inline error display.
 */
export const AUTH_ERROR_MESSAGES = {
  // ProviderCallback errors (adapter.on_authentication_error)
  cancelled: {
    title: 'Login Cancelled',
    message: 'You cancelled the sign-in process. Please try again when ready.',
  },
  denied: {
    title: 'Access Denied',
    message:
      'Access was denied by the authentication provider. Please try again.',
  },
  unknown: {
    title: 'Connection Failed',
    message:
      'Something went wrong while connecting your account. Please try again.',
  },
  consumer_account_not_supported: {
    title: 'Personal Microsoft Accounts Not Supported',
    message:
      'Personal Microsoft accounts are not supported. Please use a work or school account (Microsoft 365 / Entra ID).',
  },

  // OrgOnboarding errors (various OAuth flows)
  connected_other: {
    title: 'Account Already Connected',
    message:
      'This account is already connected to another user. Please use a different account or contact support.',
  },
  access_denied: {
    title: 'Access Denied',
    message: 'Access was denied by the provider. Please try connecting again.',
  },
  invalid_request: {
    title: 'Invalid Request',
    message: 'Invalid connection request. Please try again.',
  },
} as const;

/**
 * Type for error codes supported by the authentication system.
 */
export type AuthErrorCode = keyof typeof AUTH_ERROR_MESSAGES;
